import type { ReactNode } from 'react'
import type { AriaHiddenContextType } from './AriaHiddenContext'
import { useCallback, useRef, useState } from 'react'
import { AriaHiddenContext } from './AriaHiddenContext'

export function AriaHiddenProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [hiddenCount, setHiddenCount] = useState(0)
  const mainElementRef = useRef<HTMLElement | null>(null)

  const hideBackground = useCallback(() => {
    mainElementRef.current ??= document.querySelector('main')
      || document.querySelector('[role="main"]')
      || document.body.firstElementChild as HTMLElement

    if (mainElementRef.current) {
      mainElementRef.current.setAttribute('aria-hidden', 'true')
      mainElementRef.current.setAttribute('inert', '')
    }

    setHiddenCount(count => count + 1)
  }, [])

  const showBackground = useCallback(() => {
    setHiddenCount((count) => {
      const newCount = Math.max(0, count - 1)

      if (newCount === 0 && mainElementRef.current) {
        mainElementRef.current.removeAttribute('aria-hidden')
        mainElementRef.current.removeAttribute('inert')
      }

      return newCount
    })
  }, [])

  const isBackgroundHidden = useCallback(() => hiddenCount > 0, [hiddenCount])

  const value: AriaHiddenContextType = {
    hideBackground,
    showBackground,
    isBackgroundHidden,
  }

  return (
    <AriaHiddenContext value={value}>
      {children}
    </AriaHiddenContext>
  )
}
